/**
 * Intl formatters, built once per locale and options and then reused (2026-10-19).
 *
 * WHY. Building a formatter is the slow part of formatting: `new Intl.NumberFormat(…)` and
 * `date.toLocaleDateString(locale, options)` build a fresh one on every call. An event page
 * formats a few hundred prices and dates per render (the board, the daily price table, the
 * FAQ, the cards), and on a phone that was the largest single cost of a click to an event
 * page: ~300 ms of main thread at a 4x CPU slowdown, before the board could show (profile of
 * tixinfo.com, Oct 19). A formatter's output depends only on its locale and options (and, with
 * no `timeZone` option, the device's zone: deviceZone below), so a kept one prints exactly what a
 * fresh one would.
 *
 * A bad option (an unknown time zone) still throws from the constructor, as before, and nothing
 * that threw is kept. The maps are bounded: they hold a handful of entries in practice.
 */
const MAX = 256;
const dates = new Map<string, Intl.DateTimeFormat>();
const numbers = new Map<string, Intl.NumberFormat>();

const keep = <F>(map: Map<string, F>, key: string, make: () => F): F => {
  let f = map.get(key);
  if (f === undefined) {
    f = make();
    if (map.size >= MAX) map.clear();
    map.set(key, f);
  }
  return f;
};

/**
 * The device's time zone, cheaply: its UTC offsets on two days of 2000 and now. A date formatter
 * built without `timeZone` takes the device's zone once, when it is built, while the Dates it is
 * given (`new Date(2000, 0, 1, h, m)`, `new Date('…T12:00:00')`) follow the zone of the moment, so a
 * formatter kept across a zone change (travel; the phone's clock settings) printed 19:30 as 13:30 and
 * a Saturday as Friday until a reload (review, 2026-10-19). Formatters are kept per zone instead:
 * a new zone builds new ones, the old zone's come back if the visitor returns to it.
 */
const deviceZone = () =>
  `~${new Date(2000, 0, 1).getTimezoneOffset()},${new Date(2000, 6, 1).getTimezoneOffset()},${new Date().getTimezoneOffset()}`;

/** `new Intl.DateTimeFormat(locale, opts)`, kept (per device zone when `opts` names none). */
export const dateFormat = (locale: string | undefined, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
  keep(dates, `${String(locale)}|${opts.timeZone ?? deviceZone()}|${JSON.stringify(opts)}`, () => new Intl.DateTimeFormat(locale, opts));

/** `new Intl.NumberFormat(locale, opts)`, kept. */
export const numberFormat = (locale: string | undefined, opts: Intl.NumberFormatOptions): Intl.NumberFormat =>
  keep(numbers, `${String(locale)}|${JSON.stringify(opts)}`, () => new Intl.NumberFormat(locale, opts));

/**
 * `d.toLocaleDateString(locale, opts)` — or `toLocaleTimeString` / `toLocaleString` — with a kept
 * formatter. The same text when `opts` names the fields to print (every caller does: the
 * toLocale… methods only add default fields when none is named), and "Invalid Date" for an
 * invalid date, as those methods return (a formatter would throw).
 */
export const localeDate = (d: Date, locale: string | undefined, opts: Intl.DateTimeFormatOptions): string =>
  Number.isNaN(d.getTime()) ? 'Invalid Date' : dateFormat(locale, opts).format(d);
